/** 与 new-api option 中 GroupRatio / ModelRatio 等 JSON 字符串互转 */

export const GROUP_RATIO_KEYS = [
  'GroupRatio',
  'UserUsableGroups',
  'GroupGroupRatio',
  'AutoGroups',
  'DefaultUseAutoGroup',
  'group_ratio_setting.group_special_usable_group',
] as const

export const MODEL_PRICING_KEYS = [
  'ModelPrice',
  'ModelRatio',
  'CompletionRatio',
  'CacheRatio',
  'CreateCacheRatio',
  'ImageRatio',
  'AudioRatio',
  'AudioCompletionRatio',
  'ExposeRatioEnabled',
] as const

export const RATIO_SETTING_KEYS = [...GROUP_RATIO_KEYS, ...MODEL_PRICING_KEYS] as const

export type RatioSettingKey = (typeof RATIO_SETTING_KEYS)[number]

export type ModelPricingFilter = 'all' | 'unset' | 'conflict' | 'extended'

export interface GroupRow {
  id: string
  key: string
  displayName: string
  ratio: number
}

export interface CrossGroupRow {
  id: string
  userGroup: string
  usingGroup: string
  ratio: number
}

export interface ModelRow {
  id: string
  model: string
  value: number
}

export interface ParseIssue {
  key: string
  message: string
}

export interface RatioWarning {
  key: string
  message: string
}

export interface ModelPricingRow {
  id: string
  model: string
  modelPrice: number | null
  modelRatio: number | null
  completionRatio: number | null
  cacheRatio: number | null
  createCacheRatio: number | null
  imageRatio: number | null
  audioRatio: number | null
  audioCompletionRatio: number | null
  fromEnabledModel?: boolean
}

export interface ModelPricingMaps {
  ModelPrice: Record<string, number>
  ModelRatio: Record<string, number>
  CompletionRatio: Record<string, number>
  CacheRatio: Record<string, number>
  CreateCacheRatio: Record<string, number>
  ImageRatio: Record<string, number>
  AudioRatio: Record<string, number>
  AudioCompletionRatio: Record<string, number>
}

const MODEL_NUMBER_FIELDS: Array<keyof ModelPricingMaps> = [
  'ModelPrice',
  'ModelRatio',
  'CompletionRatio',
  'CacheRatio',
  'CreateCacheRatio',
  'ImageRatio',
  'AudioRatio',
  'AudioCompletionRatio',
]

let rowIdCounter = 0

export function createRatioRowId(prefix = 'ratio-row') {
  rowIdCounter += 1
  return `${prefix}-${Date.now()}-${rowIdCounter}`
}

function safeParseObject(raw: string): Record<string, unknown> | null {
  const s = raw?.trim()
  if (!s) return {}
  try {
    const v = JSON.parse(s) as unknown
    if (v === null || typeof v !== 'object' || Array.isArray(v)) return null
    return v as Record<string, unknown>
  } catch {
    return null
  }
}

export function getObjectParseIssue(key: string, raw: string): ParseIssue | null {
  const s = raw?.trim()
  if (!s) return null
  try {
    const v = JSON.parse(s) as unknown
    if (v === null || typeof v !== 'object' || Array.isArray(v)) {
      return { key, message: `${key} 必须是 JSON 对象` }
    }
    return null
  } catch (err) {
    return { key, message: `${key} 不是合法 JSON：${err instanceof Error ? err.message : '解析失败'}` }
  }
}

export function parseNumberMap(raw: string): Record<string, number> {
  const o = safeParseObject(raw)
  if (o === null) return {}
  const out: Record<string, number> = {}
  for (const [k, v] of Object.entries(o)) {
    if (!k) continue
    const n = typeof v === 'number' ? v : Number(v)
    if (Number.isFinite(n)) out[k] = n
  }
  return out
}

export function parseBooleanOption(raw: string, fallback = false): boolean {
  if (raw === undefined || raw === null || raw === '') return fallback
  return raw === 'true' || raw === '1'
}

export function parseStringMap(raw: string): Record<string, string> {
  const o = safeParseObject(raw)
  if (o === null) return {}
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(o)) {
    if (!k) continue
    out[k] = v == null ? '' : String(v)
  }
  return out
}

export function mergeGroupRows(
  ratioMap: Record<string, number>,
  nameMap: Record<string, string>,
  nextId: () => string
): GroupRow[] {
  const keys = new Set([...Object.keys(ratioMap), ...Object.keys(nameMap)])
  const sorted = [...keys].sort((a, b) => a.localeCompare(b))
  return sorted.map((key) => ({
    id: nextId(),
    key,
    displayName: nameMap[key] ?? '',
    ratio: ratioMap[key] !== undefined ? ratioMap[key] : 1,
  }))
}

export function groupRowsToMaps(rows: GroupRow[]): {
  groupRatio: Record<string, number>
  userUsable: Record<string, string>
} {
  const groupRatio: Record<string, number> = {}
  const userUsable: Record<string, string> = {}
  for (const r of rows) {
    const k = r.key.trim()
    if (!k) continue
    groupRatio[k] = r.ratio
    const displayName = r.displayName.trim()
    if (displayName) userUsable[k] = displayName
  }
  return { groupRatio, userUsable }
}

export function parseCrossGroupRows(raw: string, nextId: () => string): CrossGroupRow[] {
  const o = safeParseObject(raw)
  if (o === null) return []
  const rows: CrossGroupRow[] = []
  for (const [userGroup, inner] of Object.entries(o)) {
    if (!userGroup || typeof inner !== 'object' || inner === null || Array.isArray(inner)) continue
    for (const [usingGroup, rv] of Object.entries(inner as Record<string, unknown>)) {
      if (!usingGroup) continue
      const n = typeof rv === 'number' ? rv : Number(rv)
      if (!Number.isFinite(n)) continue
      rows.push({ id: nextId(), userGroup, usingGroup, ratio: n })
    }
  }
  rows.sort((a, b) =>
    `${a.userGroup}\0${a.usingGroup}`.localeCompare(`${b.userGroup}\0${b.usingGroup}`)
  )
  return rows
}

export function crossRowsToNested(rows: CrossGroupRow[]): Record<string, Record<string, number>> {
  const acc: Record<string, Record<string, number>> = {}
  for (const r of rows) {
    const ug = r.userGroup.trim()
    const cg = r.usingGroup.trim()
    if (!ug || !cg) continue
    if (!acc[ug]) acc[ug] = {}
    acc[ug][cg] = r.ratio
  }
  return acc
}

export function parseModelRows(raw: string, nextId: () => string): ModelRow[] {
  const m = parseNumberMap(raw)
  const entries = Object.entries(m).sort(([a], [b]) => a.localeCompare(b))
  return entries.map(([model, value]) => ({ id: nextId(), model, value }))
}

export function modelRowsToMap(rows: ModelRow[]): Record<string, number> {
  const out: Record<string, number> = {}
  for (const r of rows) {
    const name = r.model.trim()
    if (!name) continue
    out[name] = r.value
  }
  return out
}

export function validateRatioForm(input: {
  groupRows: GroupRow[]
  crossRows: CrossGroupRow[]
  modelRatio: ModelRow[]
  completionRatio: ModelRow[]
  modelPrice: ModelRow[]
}): string | null {
  const seenGroup = new Set<string>()
  for (const r of input.groupRows) {
    const k = r.key.trim()
    if (!k) return '分组行中存在空的分组标识（Key），请填写或删除该行'
    if (seenGroup.has(k)) return `分组 Key 重复：${k}`
    seenGroup.add(k)
    if (!Number.isFinite(r.ratio) || r.ratio < 0) return `分组「${k}」的倍率须为 ≥ 0 的数字`
  }

  const seenCross = new Set<string>()
  for (const r of input.crossRows) {
    const ug = r.userGroup.trim()
    const cg = r.usingGroup.trim()
    if (!ug || !cg) return '「用户组 × 使用组倍率」中存在空的用户组或使用组，请填写或删除该行'
    const pair = `${ug}\0${cg}`
    if (seenCross.has(pair)) return `用户组 × 使用组 组合重复：${ug} / ${cg}`
    seenCross.add(pair)
    if (!Number.isFinite(r.ratio) || r.ratio < 0) return `组合「${ug} → ${cg}」的倍率须为 ≥ 0 的数字`
  }

  const checkModels = (rows: ModelRow[], label: string) => {
    const seen = new Set<string>()
    for (const r of rows) {
      const m = r.model.trim()
      if (!m) return `${label} 中存在空的模型名，请填写或删除该行`
      if (seen.has(m)) return `${label} 中模型名重复：${m}`
      seen.add(m)
      if (!Number.isFinite(r.value) || r.value < 0) return `${label}「${m}」的数值须为 ≥ 0 的数字`
    }
    return null
  }

  return (
    checkModels(input.modelRatio, '模型倍率 ModelRatio') ||
    checkModels(input.completionRatio, '补全倍率 CompletionRatio') ||
    checkModels(input.modelPrice, '模型固定价 ModelPrice')
  )
}

function parseStringArray(raw: string): string[] {
  const s = raw?.trim()
  if (!s) return []
  try {
    const parsed = JSON.parse(s) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed.map((item) => String(item).trim()).filter(Boolean)
  } catch {
    return []
  }
}

export function buildGroupConfigWarnings(input: {
  groupRows: GroupRow[]
  crossRows: CrossGroupRow[]
  autoGroupsRaw: string
  specialUsableRaw: string
  defaultUseAutoGroup: boolean
}): RatioWarning[] {
  const warnings: RatioWarning[] = []
  const declaredGroups = new Set<string>()
  const displayableGroups = new Set<string>()

  for (const row of input.groupRows) {
    const key = row.key.trim()
    if (!key) continue
    declaredGroups.add(key)
    if (row.displayName.trim()) displayableGroups.add(key)
  }

  const autoGroups = parseStringArray(input.autoGroupsRaw)
  if (input.defaultUseAutoGroup && autoGroups.length === 0) {
    warnings.push({
      key: 'DefaultUseAutoGroup',
      message: '已开启“默认使用 auto”，但 AutoGroups 为空；新默认令牌可能无法自动选到可用渠道。',
    })
  }

  const unknownAutoGroups = autoGroups.filter((group) => !declaredGroups.has(group))
  if (unknownAutoGroups.length > 0) {
    warnings.push({
      key: 'AutoGroups',
      message: `AutoGroups 包含未在 GroupRatio 声明的分组：${unknownAutoGroups.join('、')}。`,
    })
  }

  const hiddenAutoGroups = autoGroups.filter((group) => declaredGroups.has(group) && !displayableGroups.has(group))
  if (hiddenAutoGroups.length > 0) {
    warnings.push({
      key: 'AutoGroups',
      message: `AutoGroups 中的 ${hiddenAutoGroups.join('、')} 未设置展示名，普通用户默认不可见，需通过特殊可用分组或用户自身分组授予访问。`,
    })
  }

  const unknownCrossGroups = new Set<string>()
  for (const row of input.crossRows) {
    const userGroup = row.userGroup.trim()
    const usingGroup = row.usingGroup.trim()
    if (userGroup && !declaredGroups.has(userGroup)) unknownCrossGroups.add(userGroup)
    if (usingGroup && !declaredGroups.has(usingGroup)) unknownCrossGroups.add(usingGroup)
  }
  if (unknownCrossGroups.size > 0) {
    warnings.push({
      key: 'GroupGroupRatio',
      message: `交叉倍率引用了未在 GroupRatio 声明的分组：${Array.from(unknownCrossGroups).join('、')}。`,
    })
  }

  const specialMap = safeParseObject(input.specialUsableRaw)
  if (specialMap) {
    const unknownSpecialGroups = new Set<string>()
    for (const [userGroup, operations] of Object.entries(specialMap)) {
      if (userGroup && !declaredGroups.has(userGroup)) unknownSpecialGroups.add(userGroup)
      if (operations === null || typeof operations !== 'object' || Array.isArray(operations)) continue
      for (const rawKey of Object.keys(operations as Record<string, unknown>)) {
        const group = rawKey.replace(/^[-+]:/, '').trim()
        if (group && !declaredGroups.has(group)) unknownSpecialGroups.add(group)
      }
    }
    if (unknownSpecialGroups.size > 0) {
      warnings.push({
        key: 'group_ratio_setting.group_special_usable_group',
        message: `特殊可用分组引用了未在 GroupRatio 声明的分组：${Array.from(unknownSpecialGroups).join('、')}。`,
      })
    }
  }

  return warnings
}

export function parseModelPricingMaps(values: Record<string, string>): {
  maps: ModelPricingMaps
  issues: ParseIssue[]
} {
  const issues: ParseIssue[] = []
  const maps = MODEL_NUMBER_FIELDS.reduce((acc, key) => {
    const raw = values[key] ?? ''
    const issue = getObjectParseIssue(key, raw)
    if (issue) issues.push(issue)
    acc[key] = parseNumberMap(raw)
    return acc
  }, {} as ModelPricingMaps)
  return { maps, issues }
}

export function buildModelPricingRows(
  maps: ModelPricingMaps,
  nextId: () => string,
  enabledModels: string[] = []
): ModelPricingRow[] {
  const names = new Set<string>()
  MODEL_NUMBER_FIELDS.forEach((field) => {
    Object.keys(maps[field]).forEach((name) => names.add(name))
  })
  enabledModels.forEach((name) => {
    if (name.trim()) names.add(name.trim())
  })

  return [...names]
    .sort((a, b) => a.localeCompare(b))
    .map((model) => ({
      id: nextId(),
      model,
      modelPrice: maps.ModelPrice[model] ?? null,
      modelRatio: maps.ModelRatio[model] ?? null,
      completionRatio: maps.CompletionRatio[model] ?? null,
      cacheRatio: maps.CacheRatio[model] ?? null,
      createCacheRatio: maps.CreateCacheRatio[model] ?? null,
      imageRatio: maps.ImageRatio[model] ?? null,
      audioRatio: maps.AudioRatio[model] ?? null,
      audioCompletionRatio: maps.AudioCompletionRatio[model] ?? null,
      fromEnabledModel: enabledModels.includes(model),
    }))
}

export function emptyModelPricingRow(model = ''): ModelPricingRow {
  return {
    id: createRatioRowId('model'),
    model,
    modelPrice: null,
    modelRatio: null,
    completionRatio: null,
    cacheRatio: null,
    createCacheRatio: null,
    imageRatio: null,
    audioRatio: null,
    audioCompletionRatio: null,
  }
}

export function modelPricingRowsToMaps(rows: ModelPricingRow[]): ModelPricingMaps {
  const maps: ModelPricingMaps = {
    ModelPrice: {},
    ModelRatio: {},
    CompletionRatio: {},
    CacheRatio: {},
    CreateCacheRatio: {},
    ImageRatio: {},
    AudioRatio: {},
    AudioCompletionRatio: {},
  }

  rows.forEach((row) => {
    const model = row.model.trim()
    if (!model) return
    MODEL_NUMBER_FIELDS.forEach((field) => {
      const prop = modelPricingFieldToRowProp(field)
      const value = row[prop]
      if (typeof value === 'number' && Number.isFinite(value)) {
        maps[field][model] = value
      }
    })
  })

  return maps
}

export function modelPricingFieldToRowProp(field: keyof ModelPricingMaps): keyof ModelPricingRow {
  const map: Record<keyof ModelPricingMaps, keyof ModelPricingRow> = {
    ModelPrice: 'modelPrice',
    ModelRatio: 'modelRatio',
    CompletionRatio: 'completionRatio',
    CacheRatio: 'cacheRatio',
    CreateCacheRatio: 'createCacheRatio',
    ImageRatio: 'imageRatio',
    AudioRatio: 'audioRatio',
    AudioCompletionRatio: 'audioCompletionRatio',
  }
  return map[field]
}

export function stringifyModelPricingMaps(maps: ModelPricingMaps): Record<keyof ModelPricingMaps, string> {
  return MODEL_NUMBER_FIELDS.reduce(
    (acc, key) => {
      acc[key] = JSON.stringify(maps[key])
      return acc
    },
    {} as Record<keyof ModelPricingMaps, string>
  )
}

export function hasBasePricing(row: ModelPricingRow): boolean {
  return row.modelPrice !== null || row.modelRatio !== null
}

export function hasExtendedPricing(row: ModelPricingRow): boolean {
  return (
    row.cacheRatio !== null ||
    row.createCacheRatio !== null ||
    row.imageRatio !== null ||
    row.audioRatio !== null ||
    row.audioCompletionRatio !== null
  )
}

export function hasPricingConflict(row: ModelPricingRow): boolean {
  return row.modelPrice !== null && (
    row.modelRatio !== null ||
    row.completionRatio !== null ||
    hasExtendedPricing(row)
  )
}

export function filterModelPricingRows(
  rows: ModelPricingRow[],
  query: string,
  filter: ModelPricingFilter
): ModelPricingRow[] {
  const q = query.trim().toLowerCase()
  return rows.filter((row) => {
    if (q && !row.model.toLowerCase().includes(q)) return false
    if (filter === 'unset') return !hasBasePricing(row)
    if (filter === 'conflict') return hasPricingConflict(row)
    if (filter === 'extended') return hasExtendedPricing(row)
    return true
  })
}

export function validateModelPricingRows(rows: ModelPricingRow[]): string | null {
  const seen = new Set<string>()
  for (const row of rows) {
    const model = row.model.trim()
    if (!model) return '模型定价表中存在空模型名，请填写或删除该行'
    if (seen.has(model)) return `模型定价表中模型名重复：${model}`
    seen.add(model)

    const values = [
      row.modelPrice,
      row.modelRatio,
      row.completionRatio,
      row.cacheRatio,
      row.createCacheRatio,
      row.imageRatio,
      row.audioRatio,
      row.audioCompletionRatio,
    ]
    if (values.some((value) => value !== null && (!Number.isFinite(value) || value < 0))) {
      return `模型「${model}」存在小于 0 或非法的倍率/价格`
    }
    if (hasPricingConflict(row)) {
      return `模型「${model}」同时配置了固定价和倍率/扩展项，请选择一种计费模式`
    }
  }
  return null
}

export function summarizeRatioHealth(input: {
  groupRows: GroupRow[]
  modelRows: ModelPricingRow[]
  enabledModels: string[]
  exposeRatioEnabled: boolean
}) {
  const usableGroups = input.groupRows.filter((row) => row.displayName.trim()).length
  const conflicts = input.modelRows.filter(hasPricingConflict).length
  const unsetEnabledModels = input.enabledModels.filter((model) =>
    input.modelRows.every((row) => row.model !== model || !hasBasePricing(row))
  ).length
  return {
    groupCount: input.groupRows.length,
    usableGroupCount: usableGroups,
    modelCount: input.modelRows.filter((row) => hasBasePricing(row) || hasExtendedPricing(row)).length,
    conflictCount: conflicts,
    unsetEnabledModelCount: unsetEnabledModels,
    exposeRatioEnabled: input.exposeRatioEnabled,
  }
}
